import { ValidationErrors } from '../../../models/ValidationErrors';
import { allSourceTypeOptions } from './allSourceTypeOptions';
import { LiveProgramFormPermissions } from './LiveProgramFormPermissions';
import { LiveProgram, LiveProgramMetadata, SourceType } from '../../../models/channel';
import { Permissions } from '../../../models/Permissions';
import * as _ from 'lodash';
import { ValidationError } from '../../../models/ValidationError';
import { FuelEntityType } from '../../../services';
import { Category, Item, ThumbnailResourceData } from '../../../models';


// const defaultLiveProgram: LiveProgram = {
//   name: null,
//   weekdays: null,
//   createdDate: null,
//   modifiedDate: null,
//   enabled: true,
//   createdBy: null,
//   hasSCTE: false,
//   recordForReplay: false,
//   hasGeofencing: false,
//   zipcodes: null,
//   isExternalSource: false,
//   sourceUrl: null,
//   ouId: null,
//   ouName: null,
//   timezone: null,
//   modifiedBy: null,
//   startupProgramClipId: null,
//   isCutToLive: false,
//   isDeleted: false,
//   metadata: { invertSCTE: false, sourceType: 'RTMP Source' },
//   id: null,
//   entityType: 'LiveProgram',
// };

export class LiveProgramForm {
  mode: 'new' | 'existing' = 'new';
  permissions: LiveProgramFormPermissions = new LiveProgramFormPermissions(new Permissions([]));
  hlsSourceEnabled: boolean = false; // comes from a feature flag...

  errors: ValidationErrors = new ValidationErrors([]);

  id: string = '';
  ouId: string = '';
  name: string = '';
  timezone: string | null = null;
  sourceType: { id: SourceType; name: string } | undefined = allSourceTypeOptions.find((o) => o.id === 'RTMP Source');
  sourceTypeId: string = 'RTMP Source';
  streamingEngineId: string = '';
  sourceUrl: string = '';
  enabled: boolean = false;
  isCutToLive: boolean = false;
  recordForReplay: boolean = true;
  hasSCTE: boolean = true;
  invertSCTETrigger: boolean = false;
  hasGeofencing: boolean = false;
  isSaving: boolean = false;
  autoCreateClipGroup: boolean = false;
  zipCodes: string = '';
  streamIdentifier: string = '';
  categories: Category[] = [];
  tags: string[] = [];
  metadata: LiveProgramMetadata;
  description: string = '';
  ingestFile: File | null = null;
  thumbnailSrc: string = null;
  thumbnail: ThumbnailResourceData = null;
  // Live Transition
  startupProgramClipId: string | null = null;

  liveProgramData: LiveProgram | null = null;

  static create(permissions: Permissions, hlsSourceEnabled: boolean, ouId: string): LiveProgramForm {
    const form = new LiveProgramForm();
    form.mode = 'new';
    form.permissions = new LiveProgramFormPermissions();
    form.permissions.load(permissions);
    form.hlsSourceEnabled = hlsSourceEnabled;
    form.ouId = ouId;
    form.id = '';
    form.streamIdentifier = '';
    form.metadata = {
      blackAndSilentSeconds: 30,
      silentOnlySeconds: 60,
      skipSignalDetectionOnStart: false,
    } as LiveProgramMetadata;
    // console.log('aalt form', form);
    return form;
  }

  static createFromData(
    data: LiveProgram,
    permissions: Permissions,
    hlsSourceEnabled: boolean = false,
    mode: string
  ): LiveProgramForm {
    const form = new LiveProgramForm();
    form.mode = mode === 'existing' ? 'existing' : 'new';
    form.permissions = new LiveProgramFormPermissions();
    form.permissions.load(permissions);
    form.hlsSourceEnabled = hlsSourceEnabled;
    form.loadLiveProgramData(data);
    return form;
  }

  // not a valid state for an outside caller but it is not convenient to change the type in the store so we live with this...
  constructor() {}

  loadLiveProgramData(data: LiveProgram | LiveProgramForm) {
    // console.log('loadLiveProgramData', { data });

    const resolveSourceType = () => {
      if (data.sourceType) {
        return allSourceTypeOptions.find((o) => o.id === data.sourceType.id);
      }
      const id: SourceType = data.sourceUrl?.trim() ? 'HLS Source' : 'RTMP Source';
      return allSourceTypeOptions.find((o) => o.id === id);
    };

    const resolveInvertSCTETrigger = () => {
      if (data.invertSCTETrigger) {
        return data.invertSCTETrigger;
      }
      // @ts-ignore - "metadata" not part of the type but it appears in the real payload...
      const metadata = data.metadata;
      if (metadata && metadata.invertSCTE) {
        return true;
      } else {
        return false;
      }
    };

    this.liveProgramData = data;
    this.metadata = {
      ...(data.metadata ?? {}),
      blackAndSilentSeconds: data.metadata?.blackAndSilentSeconds > 0 ? data.metadata.blackAndSilentSeconds : 30,
      silentOnlySeconds: data.metadata?.silentOnlySeconds > 0 ? data.metadata.silentOnlySeconds : 60,
      skipSignalDetectionOnStart: Boolean(data.metadata?.skipSignalDetectionOnStart),
    } as LiveProgramMetadata;

    if (data.id) {
      this.streamIdentifier = 'p-' + data.id;
    }

    this.id = data.id;
    this.ouId = data.ouId;
    this.name = data.name;
    this.timezone = data.timezone;
    this.sourceType = resolveSourceType();
    this.sourceTypeId = this.sourceType.id;
    this.streamingEngineId = data.streamingEngineId;
    this.sourceUrl = data.sourceUrl ?? '';

    this.enabled = data.enabled;
    this.isCutToLive = data.isCutToLive;
    this.recordForReplay = data.recordForReplay;
    this.hasSCTE = data.hasSCTE;
    this.invertSCTETrigger = resolveInvertSCTETrigger();
    this.hasGeofencing = data.hasGeofencing;
    this.zipcodes = data.zipcodes;
    this.description = data.description;
    if (this.isCutToLive) {
      this.startupProgramClipId = null;
    }
    if (data && data.metadata && data.metadata.thumbnail && data.metadata.thumbnail.previewUri) {
      this.thumbnailSrc = data.metadata.thumbnail.previewUri;
      this.thumbnail = data.metadata.thumbnail;
    }
  }

  validateInfoStep(form: LiveProgramForm): ValidationError[] {
    const errors: ValidationError[] = [];

    if (!form.ouId) {
      errors.push({ fieldId: 'ouId', message: 'Live Program ouId is required.' });
    }
    if (!form.name) {
      errors.push({ fieldId: 'name', message: 'Live Program name is required.' });
    }
    if (!/^[A-Za-z0-9]/.test(form.name)) {
      errors.push({ fieldId: 'name', message: 'Name should not start with a special character.' });
    }
    // if (!form.timezone) {
    //   errors.push({ fieldId: 'timezone', message: 'Live Program timezone is required.' });
    // }
    if (!form.sourceType) {
      errors.push({ fieldId: 'sourceType', message: 'Source Type is required.' });
    }
    if (form.sourceTypeId === 'HLS Source' && !form.sourceUrl) {
      errors.push({ fieldId: 'sourceUrl', message: 'sourceUrl is required for HLS Source type.' });
    }

    return errors;
  }

  validateTransitionsStep(form: LiveProgramForm): ValidationError[] {
    const errors: ValidationError[] = [];
    return errors;
  }

  validateGeofencingStep(form: LiveProgramForm): ValidationError[] {
    const errors: ValidationError[] = [];

    if (form.hasGeofencing && !form.zipcodes) {
      errors.push({ fieldId: 'zipcodes', message: 'The Zip Codes field is required!' });
    }
    return errors;
  }

  validateForm(form: LiveProgramForm): ValidationErrors {
    const validate = () => {
      let errors: ValidationError[] = [];

      errors.push(...this.validateInfoStep(form));
      errors.push(...this.validateTransitionsStep(form));
      errors.push(...this.validateGeofencingStep(form));

      return errors;
    };

    const errors = new ValidationErrors(validate());
    return errors;
  }

  // const example = {
  //   name: 'asdfa',
  //   weekdays: null,
  //   createdDate: '2022-05-25T03:54:59.57459Z',
  //   modifiedDate: null,
  //   enabled: true,
  //   createdBy: '3d092eda-1be9-48bf-92a4-f0d9d5effebb',
  //   hasSCTE: true,
  //   recordForReplay: false,
  //   hasGeofencing: true,
  //   zipcodes: 'ASDFAE',
  //   isExternalSource: false,
  //   sourceUrl: '',
  //   ouId: 'c8801903-18a3-4564-afdc-b8ce901b1038',
  //   ouName: 'Fuel Dev Env',
  //   timezone: 'America/Anguilla',
  //   modifiedBy: null,
  //   startupProgramClipId: 'af46776a-2a56-429c-82da-17996722de44',
  //   isCutToLive: true,
  //   isDeleted: false,
  //   metadata: { invertSCTE: false, sourceType: 'RTMP Source' },
  //   id: '1e4825af-f112-42a4-aa38-58780f0833c2',
  //   entityType: 'LiveProgram',
  // };
  toSaveLiveProgramRequest: (form, ouId: string, userId: string) => LiveProgram = (form, ouId, userId) => {
    // const form = this;
    // console.log('form', form);
    const buildDefaults = () => {
      if (form.mode === 'new') {
        return {
          ouId: this.ouId,
          createdBy: userId,
        };
      } else {
        if (form.liveProgramData) {
          form.liveProgramData.modifiedBy = userId;
        }
        return form.liveProgramData;
      }
    };

    const resolveZipCodes = () => {
      if (!form.hasGeofencing) return '';
      // @ts-ignore - i wonder why flatMap is coming up as unavailable?
      const normalized = form.zipcodes
        ? form.zipcodes.split(',').flatMap((s) => {
            if (s.trim().length === 0) {
              return [];
            } else {
              return [s];
            }
          })
        : null;
      return _.uniq(normalized).join(',');
    };

    const resolveMetadata = () => {
      const metadata = {
        ...(form.metadata ?? {}),
        invertSCTE: form.invertSCTETrigger,
        blackAndSilentSeconds: form.metadata?.blackAndSilentSeconds > 0 ? form.metadata.blackAndSilentSeconds : 30,
        silentOnlySeconds: form.metadata?.silentOnlySeconds > 0 ? form.metadata.silentOnlySeconds : 60,
        skipSignalDetectionOnStart: Boolean(form.metadata?.skipSignalDetectionOnStart),
      };
      // sourceType is form-local only; never persist on metadata
      delete (metadata as { sourceType?: unknown }).sourceType;
      return metadata;
    };
    const obj: LiveProgram = {
      ...buildDefaults(),

      id: form.id,
      ouId: form.ouId,
      name: form.name.trim(),
      timezone: form.timezone,
      enabled: form.enabled,
      hasGeofencing: form.hasGeofencing,
      zipcodes: resolveZipCodes(),
      hasSCTE: form.hasSCTE,
      recordForReplay: form.recordForReplay,
      sourceUrl: form.sourceUrl,
      isCutToLive: form.isCutToLive,
      metadata: resolveMetadata(),
      startupProgramClipId: form.startupProgramClipId,
      description: form.description,

      entityType: FuelEntityType.LiveProgram,
    };

    return obj;
  };
}
